import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';

function Play() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  const [ws, setWs] = useState(null);
  const [status, setStatus] = useState('connecting');
  const [question, setQuestion] = useState(null);
  const [timer, setTimer] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [score, setScore] = useState(0);
  const [finalResults, setFinalResults] = useState(null);

  const playerName = localStorage.getItem('playerName') || 'Player';
  const playerAvatar = localStorage.getItem('playerAvatar') || '😀';
  const participantId = localStorage.getItem('participantId');
  const sessionToken = localStorage.getItem('sessionToken');

  const connectWebSocket = useCallback(() => {
    if (!sessionToken) {
      navigate('/join');
      return;
    }

    const socket = api.connectWebSocket(sessionId, sessionToken, 'player');

    socket.onopen = () => {
      console.log('WebSocket connected');
      setStatus('waiting');
    };

    socket.onmessage = (event) => {
      const message = JSON.parse(event.data);
      handleMessage(message);
    };

    socket.onclose = () => {
      console.log('WebSocket disconnected');
      setStatus('disconnected');
    };

    socket.onerror = (error) => {
      console.error('WebSocket error:', error);
      setStatus('error');
    };

    setWs(socket);

    return () => socket.close();
  }, [sessionId, sessionToken, navigate]);

  useEffect(() => {
    const cleanup = connectWebSocket();
    return cleanup;
  }, [connectWebSocket]);

  useEffect(() => {
    let interval;
    if (status === 'question' && timer > 0 && !answerSubmitted) {
      interval = setInterval(() => {
        setTimer((t) => Math.max(0, t - 1));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [status, timer, answerSubmitted]);

  const handleMessage = (message) => {
    const { type, payload } = message;

    switch (type) {
      case 'connected':
        setStatus(payload.sessionStatus === 'active' ? 'question' : 'waiting');
        break;

      case 'quiz_started':
        setStatus('waiting');
        break;

      case 'question_show':
        setQuestion(payload);
        setTimer(payload.timeLimitSeconds);
        setSelectedAnswer(null);
        setAnswerSubmitted(false);
        setResult(null);
        setStatus('question');
        break;

      case 'answer_confirmed':
        setAnswerSubmitted(true);
        break;

      case 'answer_revealed':
        setResult({
          correctAnswer: payload.correctAnswer,
          stats: payload.stats
        });
        setStatus('result');
        break;

      case 'leaderboard_update':
        setLeaderboard(payload.leaderboard);
        const me = payload.leaderboard.find(p => p.participantId === participantId);
        if (me) setScore(me.score);
        setStatus('leaderboard');
        break;

      case 'quiz_ended':
        setFinalResults(payload.finalLeaderboard);
        setStatus('finished');
        break;

      case 'quiz_paused':
        setStatus('paused');
        break;

      case 'quiz_resumed':
        setStatus('question');
        break;

      default:
        console.log('Unknown message:', type, payload);
    }
  };

  const submitAnswer = (label) => {
    if (answerSubmitted || !ws) return;

    setSelectedAnswer(label);
    
    ws.send(JSON.stringify({
      type: 'answer_submit',
      payload: {
        questionIndex: question.questionIndex,
        answerLabel: label,
        timeTakenMs: (question.timeLimitSeconds - timer) * 1000
      }
    }));
  };

  const getTimerClass = () => {
    if (timer <= 3) return 'danger';
    if (timer <= 5) return 'warning';
    return '';
  };

  if (status === 'connecting') {
    return (
      <div className="play-page">
        <div className="waiting-screen">
          <div className="pulse">🔄</div>
          <h2>Connecting...</h2>
        </div>
      </div>
    );
  }

  if (status === 'disconnected' || status === 'error') {
    return (
      <div className="play-page">
        <div className="waiting-screen">
          <div>❌</div>
          <h2>Connection Lost</h2>
          <p>Please rejoin the quiz</p>
          <button className="btn btn-primary" onClick={() => navigate('/join')}>
            Rejoin
          </button>
        </div>
      </div>
    );
  }

  if (status === 'finished' && finalResults) {
    const myRank = finalResults.findIndex(p => p.participantId === participantId) + 1;
    const myResult = finalResults.find(p => p.participantId === participantId);

    return (
      <div className="play-page">
        <div className="final-results">
          <div className="trophy">🏆</div>
          <h1>Quiz Complete!</h1>
          
          <div className="podium">
            {finalResults.length >= 2 && (
              <div className="podium-place second">
                <div className="avatar">{finalResults[1].avatar}</div>
                <div className="name">{finalResults[1].name}</div>
                <div className="score">{finalResults[1].score} pts</div>
                <div>🥈</div>
              </div>
            )}
            
            {finalResults.length >= 1 && (
              <div className="podium-place first">
                <div className="avatar">{finalResults[0].avatar}</div>
                <div className="name">{finalResults[0].name}</div>
                <div className="score">{finalResults[0].score} pts</div>
                <div>🥇</div>
              </div>
            )}
            
            {finalResults.length >= 3 && (
              <div className="podium-place third">
                <div className="avatar">{finalResults[2].avatar}</div>
                <div className="name">{finalResults[2].name}</div>
                <div className="score">{finalResults[2].score} pts</div>
                <div>🥉</div>
              </div>
            )}
          </div>

          {myResult && (
            <div className="leaderboard">
              <h3>Your Result</h3>
              <div className="leaderboard-item me">
                <span className="rank">#{myRank}</span>
                <span className="leaderboard-avatar">{myResult.avatar}</span>
                <span className="leaderboard-name">{myResult.name}</span>
                <span className="leaderboard-score">{myResult.score} pts</span>
              </div>
            </div>
          )}

          <button className="btn btn-primary" style={{ marginTop: 30, maxWidth: 300 }} onClick={() => navigate('/')}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="play-page">
      <div className="play-header">
        <div className="player-info">
          <span className="player-avatar">{playerAvatar}</span>
          <span>{playerName}</span>
        </div>
        <div className="player-score">{score} pts</div>
      </div>

      {status === 'waiting' && (
        <div className="waiting-screen">
          <div className="pulse" style={{ fontSize: '4rem' }}>⏳</div>
          <h2>Waiting for host...</h2>
          <p>The quiz will start soon!</p>
        </div>
      )}

      {status === 'paused' && (
        <div className="waiting-screen">
          <div style={{ fontSize: '4rem' }}>⏸️</div>
          <h2>Quiz Paused</h2>
          <p>Waiting for host to resume...</p>
        </div>
      )}

      {(status === 'question' || status === 'result') && question && (
        <div className="question-screen">
          <div className="question-header">
            <div className="question-number">
              Question {question.questionIndex} of {question.totalQuestions}
            </div>
            {status === 'question' && (
              <div className={`timer ${getTimerClass()}`}>{timer}s</div>
            )}
          </div>

          <div className="question-text">{question.questionText}</div>

          <div className="answers-grid">
            {question.answers.map((answer) => {
              let className = 'answer-btn';
              if (selectedAnswer === answer.label) className += ' selected';
              if (status === 'result') {
                if (answer.label === result?.correctAnswer) className += ' correct';
                else if (selectedAnswer === answer.label) className += ' incorrect';
              }

              return (
                <button
                  key={answer.label}
                  className={className}
                  onClick={() => submitAnswer(answer.label)}
                  disabled={answerSubmitted || status === 'result' || timer === 0}
                >
                  <span className="answer-label">{answer.label}</span>
                  <span>{answer.text}</span>
                </button>
              );
            })}
          </div>

          {answerSubmitted && status === 'question' && (
            <div style={{ textAlign: 'center', marginTop: 20, color: '#a0a0a0' }}>
              ✓ Answer submitted! Waiting for results...
            </div>
          )}
        </div>
      )}

      {status === 'leaderboard' && (
        <div className="results-screen">
          <div className="result-feedback">
            {selectedAnswer === result?.correctAnswer ? '🎉' : '😔'}
          </div>
          <div className="points-earned">
            {selectedAnswer === result?.correctAnswer ? '+100 points!' : 'No points'}
          </div>

          <div className="leaderboard">
            <h3>Leaderboard</h3>
            {leaderboard.slice(0, 5).map((player) => (
              <div
                key={player.participantId}
                className={`leaderboard-item ${player.participantId === participantId ? 'me' : ''}`}
              >
                <span className="rank">#{player.rank}</span>
                <span className="leaderboard-avatar">{player.avatar}</span>
                <span className="leaderboard-name">{player.name}</span>
                <span className="leaderboard-score">{player.score}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Play;
